import { useState, useEffect } from 'react'
import { evaluationService } from '@/services/evaluationService'
import type { EvaluationResult, EvaluationRunSummary } from '@shared/types/evaluation'
import { toast } from 'sonner'

interface UseEvaluationResultsReturn {
  results: EvaluationResult[]
  history: EvaluationRunSummary[]
  loading: boolean
  error: string | null
  refreshResults: () => Promise<void>
  refreshHistory: () => Promise<void>
}

export const useEvaluationResults = (testSetId: string): UseEvaluationResultsReturn => {
  const [results, setResults] = useState<EvaluationResult[]>([])
  const [history, setHistory] = useState<EvaluationRunSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchResults = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [latestResults, evaluationHistory] = await Promise.all([
        evaluationService.getLatestResults(testSetId),
        evaluationService.getEvaluationHistory(testSetId, 10)
      ])
      
      // Handle the backend response structure
      const processedResults = Array.isArray(latestResults) ? latestResults : 
                             (latestResults as any)?.evaluations || []
      
      setResults(processedResults)
      setHistory(evaluationHistory)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch evaluation results'
      setError(errorMessage)
      console.error('Error fetching evaluation results:', err)
      // Set empty arrays on error
      setResults([])
      setHistory([])
    } finally {
      setLoading(false)
    }
  }

  const refreshResults = async () => {
    try {
      const latestResults = await evaluationService.getLatestResults(testSetId)
      const processedResults = Array.isArray(latestResults) ? latestResults : 
                             (latestResults as any)?.evaluations || []
      setResults(processedResults)
      toast.success('Results refreshed')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh results'
      toast.error(errorMessage)
    }
  }

  const refreshHistory = async () => {
    try {
      const evaluationHistory = await evaluationService.getEvaluationHistory(testSetId, 10)
      setHistory(evaluationHistory)
      toast.success('History refreshed')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh history'
      toast.error(errorMessage)
    }
  }

  useEffect(() => {
    if (testSetId) {
      fetchResults()
    }
  }, [testSetId])

  return {
    results,
    history,
    loading,
    error,
    refreshResults,
    refreshHistory
  }
}